<template>
    <div class="flex h-[calc(100vh-140px)] gap-6" id="message-board-app">
        <!-- If bot is not configured/available, show the configure screen/button only -->
        <div
            v-if="!team.bot"
            class="
                flex flex-col
                items-center
                justify-center
                w-full
                h-full
                bg-white
                rounded-lg
                border border-gray-100
                p-8
                shadow-sm
            "
        >
            <ion-icon
                name="logo-android"
                class="text-6xl text-purple-600 mb-4"
            ></ion-icon>
            <h3 class="text-xl font-bold text-gray-800 font-slab mb-2">
                Team Bot Not Configured
            </h3>
            <p
                v-if="canManage"
                class="text-gray-500 text-center max-w-sm text-sm mb-6"
            >
                To start chatting, please configure your team bot first.
            </p>
            <p v-else class="text-gray-500 text-center max-w-sm text-sm mb-6">
                The team bot is not configured yet. Please ask your team manager
                or owner to set it up.
            </p>

            <button
                v-if="canManage"
                type="button"
                @click="showBotSettings = true"
                class="
                    bg-purple-600
                    text-white text-sm
                    px-5
                    py-2.5
                    rounded-lg
                    hover:bg-purple-700
                    font-semibold
                    flex
                    items-center
                    gap-2
                    transition
                    shadow-sm
                "
            >
                <ion-icon name="settings-outline" class="text-lg"></ion-icon>
                Configure Team Bot
            </button>
        </div>

        <template v-else>
            <!-- Sidebar: Topics List -->
            <div
                class="
                    w-80
                    bg-white
                    rounded-lg
                    shadow-sm
                    border border-gray-100
                    flex flex-col flex-shrink-0
                "
            >
                <div
                    class="
                        p-4
                        border-b border-gray-100
                        flex
                        justify-between
                        items-center
                        flex-shrink-0
                    "
                >
                    <h4 class="font-bold text-gray-700 font-slab">
                        Conversations
                    </h4>
                    <div class="flex items-center gap-1.5">
                        <button
                            type="button"
                            @click="showBotSettings = true"
                            class="
                                bg-gray-100
                                text-gray-700 text-xs
                                px-2.5
                                py-1.5
                                rounded-lg
                                hover:bg-gray-200
                                font-semibold
                                flex
                                items-center
                                gap-1
                                transition
                            "
                            title="Bot Settings"
                        >
                            <ion-icon
                                name="settings-outline"
                                class="text-sm"
                            ></ion-icon>
                        </button>
                        <button
                            type="button"
                            @click="startNewConversation"
                            class="
                                bg-purple-600
                                text-white text-xs
                                px-2.5
                                py-1.5
                                rounded-lg
                                hover:bg-purple-700
                                font-semibold
                                flex
                                items-center
                                gap-1
                                transition
                            "
                        >
                            <ion-icon
                                name="add-circle-outline"
                                class="text-sm"
                            ></ion-icon>
                            New Topic
                        </button>
                    </div>
                </div>

                <!-- Topics List Scroll Area -->
                <div class="flex-grow overflow-y-auto p-2 space-y-1.5">
                    <div
                        v-if="topics.length === 0"
                        class="text-center text-gray-400 py-12 text-sm"
                    >
                        No conversations yet.<br />
                        Start a new topic!
                    </div>
                    <div v-else>
                        <button
                            v-for="t in topics"
                            :key="t.id"
                            @click="selectTopic(t)"
                            class="
                                w-full
                                text-left
                                block
                                p-3
                                rounded-lg
                                hover:bg-purple-50
                                transition
                                border
                            "
                            :class="
                                activeTopic && activeTopic.id === t.id
                                    ? 'bg-purple-50/70 border-purple-200 text-purple-900 font-semibold'
                                    : 'border-transparent text-gray-700'
                            "
                        >
                            <div class="truncate text-sm">{{ t.title }}</div>
                            <div
                                class="
                                    flex
                                    justify-between
                                    items-center
                                    mt-1.5
                                    text-[10px] text-gray-400
                                    font-sans
                                "
                            >
                                <span
                                    >by
                                    {{ t.user ? t.user.name : "User" }}</span
                                >
                                <span
                                    v-if="topicTokens(t) > 0"
                                    class="
                                        text-purple-600
                                        font-semibold
                                        bg-purple-50
                                        px-1.5
                                        py-0.5
                                        rounded
                                        text-[9px]
                                        shrink-0
                                    "
                                >
                                    {{ topicTokens(t).toLocaleString() }} tokens
                                </span>
                                <span>{{ formatTimeAgo(t.updated_at) }}</span>
                            </div>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Chat Area -->
            <div
                class="
                    flex-grow
                    bg-white
                    rounded-lg
                    shadow-sm
                    border border-gray-100
                    flex flex-col
                    overflow-hidden
                    relative
                "
            >
                <div
                    v-if="activeTopic"
                    class="flex flex-col h-full overflow-hidden"
                >
                    <!-- Chat Header -->
                    <div
                        class="
                            p-4
                            border-b border-gray-100
                            flex
                            items-center
                            justify-between
                            flex-shrink-0
                            bg-gray-50/50
                        "
                    >
                        <div class="flex items-center gap-3">
                            <img
                                :src="botAvatarUrl"
                                alt="Bot"
                                class="
                                    w-10
                                    h-10
                                    rounded-full
                                    border border-purple-100
                                    object-cover
                                "
                            />
                            <div>
                                <h4
                                    class="
                                        font-bold
                                        text-gray-800 text-sm
                                        font-sans
                                    "
                                >
                                    {{ activeTopic.title }}
                                </h4>
                                <p
                                    class="
                                        text-xs text-gray-400
                                        flex
                                        items-center
                                        gap-1.5
                                        flex-wrap
                                    "
                                >
                                    <span>
                                        Chatting with
                                        {{ team.bot ? team.bot.name : "Bot" }}
                                        ({{ displayProvider }})
                                    </span>
                                    <span
                                        v-if="topicTokens(activeTopic) > 0"
                                        class="
                                            font-semibold
                                            text-purple-600
                                            bg-purple-50
                                            px-1.5
                                            py-0.5
                                            rounded
                                            text-[10px]
                                            inline-flex
                                            items-center
                                        "
                                    >
                                        Usage:
                                        {{
                                            topicTokens(
                                                activeTopic
                                            ).toLocaleString()
                                        }}
                                        tokens
                                    </span>
                                </p>
                            </div>
                        </div>

                        <button
                            v-if="canDelete(activeTopic)"
                            type="button"
                            @click="deleteTopic(activeTopic)"
                            class="
                                text-gray-400
                                hover:text-red-500
                                transition
                                p-2
                                rounded-full
                                hover:bg-red-50
                                flex
                                items-center
                                justify-center
                            "
                            title="Delete Conversation"
                        >
                            <ion-icon
                                name="trash-outline"
                                class="text-lg"
                            ></ion-icon>
                        </button>
                    </div>

                    <!-- Messages Window -->
                    <div
                        class="flex-grow overflow-y-auto p-6 space-y-4"
                        ref="messagesContainer"
                    >
                        <div v-for="msg in activeTopic.messages" :key="msg.id">
                            <!-- User Message (Right Aligned) -->
                            <div
                                v-if="msg.sender_type === 'user'"
                                class="flex items-start gap-3 justify-end"
                            >
                                <div class="max-w-[70%]">
                                    <div
                                        class="
                                            text-[10px] text-gray-400 text-right
                                            mb-1
                                        "
                                    >
                                        {{ msg.user ? msg.user.name : "User" }}
                                        &bull; {{ formatTime(msg.created_at) }}
                                    </div>
                                    <div
                                        class="
                                            bg-purple-600
                                            text-white
                                            p-3.5
                                            rounded-2xl rounded-tr-none
                                            shadow-sm
                                            text-sm
                                            whitespace-pre-line
                                            leading-relaxed
                                        "
                                    >
                                        {{ msg.message }}
                                    </div>
                                </div>
                            </div>

                            <!-- Bot Message (Left Aligned) -->
                            <div v-else class="flex items-start gap-3">
                                <img
                                    :src="botAvatarUrl"
                                    alt="Bot"
                                    class="
                                        w-8
                                        h-8
                                        rounded-full
                                        border border-purple-100
                                        object-cover
                                        flex-shrink-0
                                        mt-4
                                    "
                                />
                                <div class="max-w-[70%]">
                                    <div class="text-[10px] text-gray-400 mb-1">
                                        {{ team.bot ? team.bot.name : "Bot" }}
                                        &bull; {{ formatTime(msg.created_at) }}
                                    </div>
                                    <div
                                        class="
                                            bg-gray-100
                                            text-gray-800
                                            p-3.5
                                            rounded-2xl rounded-tl-none
                                            shadow-sm
                                            text-sm
                                            leading-relaxed
                                            markdown-body
                                        "
                                        v-html="renderMarkdown(msg.message)"
                                    ></div>
                                </div>
                            </div>
                        </div>

                        <!-- Typing Indicator -->
                        <div v-if="isTyping" class="flex items-start gap-3">
                            <img
                                :src="botAvatarUrl"
                                alt="Bot"
                                class="
                                    w-8
                                    h-8
                                    rounded-full
                                    border border-purple-100
                                    object-cover
                                    flex-shrink-0
                                    mt-1
                                "
                            />
                            <div class="max-w-[70%]">
                                <div class="text-[10px] text-gray-400 mb-1">
                                    {{ team.bot ? team.bot.name : "Bot" }} is
                                    typing...
                                </div>
                                <div
                                    class="
                                        bg-gray-100
                                        text-gray-800
                                        px-4
                                        py-3
                                        rounded-2xl rounded-tl-none
                                        shadow-sm
                                        flex
                                        items-center
                                        gap-1.5
                                        h-10
                                        w-16
                                        justify-center
                                    "
                                >
                                    <span
                                        class="
                                            dot-typing
                                            bg-gray-500
                                            rounded-full
                                            w-2
                                            h-2
                                            animate-bounce
                                        "
                                        style="animation-delay: 0.1s"
                                    ></span>
                                    <span
                                        class="
                                            dot-typing
                                            bg-gray-500
                                            rounded-full
                                            w-2
                                            h-2
                                            animate-bounce
                                        "
                                        style="animation-delay: 0.2s"
                                    ></span>
                                    <span
                                        class="
                                            dot-typing
                                            bg-gray-500
                                            rounded-full
                                            w-2
                                            h-2
                                            animate-bounce
                                        "
                                        style="animation-delay: 0.3s"
                                    ></span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Chat Footer/Input Form -->
                    <div
                        class="
                            p-4
                            border-t border-gray-100
                            bg-white
                            flex-shrink-0
                        "
                    >
                        <form
                            @submit.prevent="sendMessage"
                            class="flex gap-2.5 items-end"
                        >
                            <div class="flex-grow relative">
                                <textarea
                                    v-model="userInput"
                                    @keydown.enter.exact.prevent="sendMessage"
                                    required
                                    rows="1"
                                    placeholder="Type your message here..."
                                    class="
                                        w-full
                                        pl-4
                                        pr-12
                                        py-3
                                        border border-gray-200
                                        rounded-2xl
                                        focus:outline-none
                                        focus:ring-2
                                        focus:ring-purple-500
                                        focus:border-purple-500
                                        resize-none
                                        text-sm
                                        transition
                                        max-h-32
                                        shadow-inner
                                        bg-gray-50
                                    "
                                    ref="chatTextarea"
                                    :disabled="isTyping"
                                ></textarea>
                            </div>
                            <markdown-upload-button
                                size="md"
                                :disabled="isTyping"
                                @upload="
                                    (t) =>
                                        (userInput =
                                            (userInput
                                                ? userInput.trim() + '\n\n'
                                                : '') + t)
                                "
                            ></markdown-upload-button>
                            <mic-button
                                size="md"
                                :disabled="isTyping"
                                @listen-start="
                                    voiceBaseChat = userInput
                                        ? userInput.trim() + ' '
                                        : ''
                                "
                                @transcript="
                                    (t) => (userInput = voiceBaseChat + t)
                                "
                                @listen-end="onVoiceEnd($event, 'chat')"
                            ></mic-button>
                            <button
                                v-if="isTyping"
                                type="button"
                                @click="killAiProcess"
                                class="
                                    bg-red-500
                                    text-white
                                    rounded-2xl
                                    p-3
                                    flex
                                    items-center
                                    justify-center
                                    hover:bg-red-600
                                    transition
                                    shadow-sm
                                    h-[46px]
                                    w-[46px]
                                    flex-shrink-0
                                "
                                title="Kill AI Process"
                            >
                                <ion-icon
                                    name="stop-outline"
                                    class="text-xl"
                                ></ion-icon>
                            </button>
                            <button
                                v-else
                                type="submit"
                                :disabled="isTyping || !userInput.trim()"
                                class="
                                    bg-purple-600
                                    text-white
                                    rounded-2xl
                                    p-3
                                    flex
                                    items-center
                                    justify-center
                                    hover:bg-purple-700
                                    transition
                                    shadow-sm
                                    h-[46px]
                                    w-[46px]
                                    flex-shrink-0
                                    disabled:opacity-50
                                "
                            >
                                <ion-icon
                                    name="send"
                                    class="text-xl"
                                ></ion-icon>
                            </button>
                        </form>
                    </div>
                </div>

                <!-- No Active Conversation/Topic -->
                <div
                    v-else
                    class="
                        flex-grow flex flex-col
                        items-center
                        justify-center
                        p-12
                        text-center
                        h-full
                    "
                >
                    <img
                        :src="botAvatarUrl"
                        alt="Bot"
                        class="
                            w-20
                            h-20
                            rounded-full
                            border-2 border-purple-200
                            shadow-md
                            object-cover
                            mb-4
                        "
                    />
                    <h3 class="text-lg font-bold text-gray-700 font-slab">
                        Meet {{ team.bot ? team.bot.name : "Bot" }}
                    </h3>
                    <p class="text-gray-400 max-w-sm text-sm mt-1 mb-8">
                        I am your team's AI assistant. Send a message below to
                        start a new conversation with me!
                    </p>

                    <div
                        class="
                            w-full
                            max-w-2xl
                            bg-white
                            p-2
                            rounded-2xl
                            shadow-sm
                            border border-gray-200
                        "
                    >
                        <form
                            @submit.prevent="createTopic"
                            class="flex gap-2.5 items-end"
                        >
                            <div class="flex-grow relative">
                                <textarea
                                    v-model="newTopicInput"
                                    @keydown.enter.exact.prevent="createTopic"
                                    required
                                    rows="1"
                                    placeholder="Type your first message here to start..."
                                    class="
                                        w-full
                                        pl-4
                                        pr-12
                                        py-3
                                        border-none
                                        rounded-2xl
                                        focus:outline-none focus:ring-0
                                        resize-none
                                        text-sm
                                        transition
                                        max-h-32
                                        bg-transparent
                                    "
                                    ref="newTopicTextarea"
                                    :disabled="isTyping"
                                ></textarea>
                            </div>
                            <markdown-upload-button
                                size="sm"
                                class="mb-0.5"
                                :disabled="isTyping"
                                @upload="
                                    (t) =>
                                        (newTopicInput =
                                            (newTopicInput
                                                ? newTopicInput.trim() + '\n\n'
                                                : '') + t)
                                "
                            ></markdown-upload-button>
                            <mic-button
                                size="sm"
                                class="mb-0.5"
                                :disabled="isTyping"
                                @listen-start="
                                    voiceBaseTopic = newTopicInput
                                        ? newTopicInput.trim() + ' '
                                        : ''
                                "
                                @transcript="
                                    (t) => (newTopicInput = voiceBaseTopic + t)
                                "
                                @listen-end="onVoiceEnd($event, 'topic')"
                            ></mic-button>
                            <button
                                v-if="isTyping"
                                type="button"
                                @click="killAiProcess"
                                class="
                                    bg-red-500
                                    text-white
                                    rounded-xl
                                    p-3
                                    flex
                                    items-center
                                    justify-center
                                    hover:bg-red-600
                                    transition
                                    shadow-sm
                                    h-[40px]
                                    w-[40px]
                                    flex-shrink-0
                                    mb-0.5
                                    mr-0.5
                                "
                                title="Kill AI Process"
                            >
                                <ion-icon
                                    name="stop-outline"
                                    class="text-lg"
                                ></ion-icon>
                            </button>
                            <button
                                v-else
                                type="submit"
                                :disabled="isTyping || !newTopicInput.trim()"
                                class="
                                    bg-purple-600
                                    text-white
                                    rounded-xl
                                    p-3
                                    flex
                                    items-center
                                    justify-center
                                    hover:bg-purple-700
                                    transition
                                    shadow-sm
                                    h-[40px]
                                    w-[40px]
                                    flex-shrink-0
                                    mb-0.5
                                    mr-0.5
                                    disabled:opacity-50
                                "
                            >
                                <ion-icon
                                    name="send"
                                    class="text-lg"
                                ></ion-icon>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </template>

        <!-- Bot Settings Modal -->
        <team-bot-manager
            v-if="showBotSettings"
            :team-id="teamId"
            :save-route="saveRoute"
            :delete-route="deleteRoute"
            :can-manage="canManage"
            :initial-bot="team.bot"
            :start-open="true"
            @close="showBotSettings = false"
        ></team-bot-manager>
    </div>
</template>

<script>
import axios from "axios";

export default {
    name: "TeamBotChat",
    props: {
        teamId: {
            required: true,
        },
        initialTopics: {
            type: Array,
            default: () => [],
        },
        initialTeam: {
            type: Object,
            required: true,
        },
        defaultAvatar: {
            type: String,
            default: "/default-images/avatar.png",
        },
        canManage: {
            type: Boolean,
            default: false,
        },
        saveRoute: {
            type: String,
            default: "",
        },
        deleteRoute: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            topics: this.initialTopics,
            team: this.initialTeam,
            showBotSettings: false,
            activeTopic: null,
            userInput: "",
            newTopicInput: "",
            voiceBaseChat: "",
            voiceBaseTopic: "",
            isTyping: false,
            currentUser: null,
            chatCancelTokenSource: null,
        };
    },
    computed: {
        botAvatarUrl() {
            if (this.team.bot && this.team.bot.avatar) {
                return this.team.bot.avatar;
            }
            return this.defaultAvatar;
        },
        displayProvider() {
            if (!this.team.bot) return "Bot";
            const provider = this.team.bot.provider;
            if (provider === "platform_default") return "Platform Default";
            if (provider === "openai") return "OpenAI";
            if (provider === "gemini") return "Gemini";
            return provider;
        },
    },
    mounted() {
        // Fetch current user & initial data just in case
        this.fetchCurrentState();

        // Select first topic by default if available
        if (this.topics.length > 0) {
            this.selectTopic(this.topics[0]);
        }
    },
    watch: {
        userInput() {
            this.adjustTextareaHeight(this.$refs.chatTextarea);
        },
        newTopicInput() {
            this.adjustTextareaHeight(this.$refs.newTopicTextarea);
        },
    },
    methods: {
        topicTokens(topic) {
            if (!topic || !topic.messages) return 0;
            return topic.messages.reduce(
                (sum, msg) => sum + (msg.total_tokens || 0),
                0
            );
        },
        killAiProcess() {
            axios.post("/plays/kill-ai").catch((err) => {
                console.error("Failed to send server-side kill signal:", err);
            });

            if (this.chatCancelTokenSource) {
                this.chatCancelTokenSource.cancel(
                    "Operation canceled by the user."
                );
                this.chatCancelTokenSource = null;
            }
            this.isTyping = false;
        },
        fetchCurrentState() {
            axios
                .get(`/teams/message-board?teamId=${this.teamId}`, {
                    headers: { Accept: "application/json" },
                })
                .then((response) => {
                    this.topics = response.data.topics;
                    this.team = response.data.team;
                    this.currentUser = response.data.currentUser;

                    // Keep active topic synced
                    if (this.activeTopic) {
                        const matched = this.topics.find(
                            (t) => t.id === this.activeTopic.id
                        );
                        if (matched) {
                            this.activeTopic = matched;
                        }
                    }
                })
                .catch((error) => {
                    console.error("Failed to sync message board state:", error);
                });
        },
        selectTopic(topic) {
            this.activeTopic = topic;
            this.newTopicInput = "";
            this.userInput = "";
            this.$nextTick(() => {
                this.scrollToBottom();
                if (this.$refs.chatTextarea) {
                    this.$refs.chatTextarea.focus();
                }
            });
        },
        startNewConversation() {
            this.activeTopic = null;
            this.$nextTick(() => {
                if (this.$refs.newTopicTextarea) {
                    this.$refs.newTopicTextarea.focus();
                }
            });
        },
        canDelete(topic) {
            if (!this.currentUser) return false;
            return (
                this.team.owner_id === this.currentUser.id ||
                topic.user_id === this.currentUser.id
            );
        },
        deleteTopic(topic) {
            if (
                !confirm(
                    "Are you sure you want to delete this conversation? This action cannot be undone."
                )
            ) {
                return;
            }

            axios
                .post(
                    `/teams/message-board/topics/${topic.id}/delete?teamId=${this.teamId}`,
                    {},
                    {
                        headers: { Accept: "application/json" },
                    }
                )
                .then(() => {
                    this.topics = this.topics.filter((t) => t.id !== topic.id);
                    if (this.activeTopic && this.activeTopic.id === topic.id) {
                        this.activeTopic =
                            this.topics.length > 0 ? this.topics[0] : null;
                    }
                })
                .catch((error) => {
                    alert("Failed to delete conversation.");
                    console.error(error);
                });
        },
        createTopic() {
            const msg = this.newTopicInput.trim();
            if (!msg) return;

            this.isTyping = true;
            this.newTopicInput = "";
            this.voiceBaseTopic = "";

            // Create temporary activeTopic so user sees their message instantly
            const tempTopic = {
                id: Date.now(),
                title: msg.length > 30 ? msg.substring(0, 30) + "..." : msg,
                user: this.currentUser,
                messages: [
                    {
                        id: Date.now(),
                        sender_type: "user",
                        user: this.currentUser,
                        message: msg,
                        created_at: new Date().toISOString(),
                    },
                ],
            };
            this.activeTopic = tempTopic;
            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.chatCancelTokenSource = axios.CancelToken.source();

            axios
                .post(
                    `/teams/message-board/topics?teamId=${this.teamId}`,
                    {
                        message: msg,
                    },
                    {
                        headers: { Accept: "application/json" },
                        cancelToken: this.chatCancelTokenSource.token,
                    }
                )
                .then((response) => {
                    const realTopic = response.data.topic;
                    // Add to topics list
                    this.topics.unshift(realTopic);
                    this.selectTopic(realTopic);
                    this.isTyping = false;
                    this.chatCancelTokenSource = null;
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        console.log("AI creation process cancelled by user.");
                        return;
                    }
                    this.activeTopic = null;
                    this.isTyping = false;
                    this.chatCancelTokenSource = null;
                    alert(
                        "Failed to start conversation. Please check your Bot settings or AI api keys."
                    );
                    console.error(error);
                });
        },
        onVoiceEnd(reason, which) {
            // Auto-submit once the coach stops talking (silence/manual/auto end).
            // Skip on error, and wait a tick so the final transcript is applied.
            if (reason === "error") return;
            this.$nextTick(() => {
                if (this.isTyping) return;
                if (which === "chat") {
                    if (this.activeTopic && this.userInput.trim()) {
                        this.sendMessage();
                    }
                } else if (this.newTopicInput.trim()) {
                    this.createTopic();
                }
            });
        },
        sendMessage() {
            const msg = this.userInput.trim();
            const topic = this.activeTopic;
            if (!msg || !topic) return;

            this.userInput = "";
            this.voiceBaseChat = "";
            this.isTyping = true;

            // Instantly append user's message
            topic.messages.push({
                id: Date.now(),
                sender_type: "user",
                user: this.currentUser,
                message: msg,
                created_at: new Date().toISOString(),
            });
            this.$nextTick(() => {
                this.scrollToBottom();
            });

            this.chatCancelTokenSource = axios.CancelToken.source();

            axios
                .post(
                    `/teams/message-board/topics/${topic.id}/messages?teamId=${this.teamId}`,
                    {
                        message: msg,
                    },
                    {
                        headers: { Accept: "application/json" },
                        cancelToken: this.chatCancelTokenSource.token,
                    }
                )
                .then((response) => {
                    topic.messages = response.data.messages;
                    this.isTyping = false;
                    this.chatCancelTokenSource = null;
                    // Update topic's updated_at order
                    this.fetchCurrentState();
                    this.$nextTick(() => {
                        this.scrollToBottom();
                    });
                })
                .catch((error) => {
                    if (axios.isCancel(error)) {
                        console.log("AI message process cancelled by user.");
                        return;
                    }
                    this.isTyping = false;
                    this.chatCancelTokenSource = null;
                    alert(
                        "AI service error. Please verify your provider API key."
                    );
                    console.error(error);
                });
        },
        renderMarkdown(text) {
            if (!text) return "";
            let html = text;

            // 1. Escape HTML first to prevent raw script injections
            html = this.escapeHtml(html);

            // 2. Code blocks
            html = html.replace(/```([\s\S]*?)```/g, (match, code) => {
                return `<pre class="bg-gray-800 text-gray-100 p-4 rounded-lg my-2 overflow-x-auto text-xs font-mono"><code>${code.trim()}</code></pre>`;
            });

            // 3. Inline code
            html = html.replace(
                /`([^`]+)`/g,
                '<code class="bg-gray-200 text-purple-700 px-1.5 py-0.5 rounded text-xs font-mono">$1</code>'
            );

            // 4. Bold
            html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");

            // 5. Italic
            html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");

            // 6. Links
            html = html.replace(
                /\[([^\]]+)\]\(([^)]+)\)/g,
                '<a href="$2" target="_blank" class="text-purple-600 hover:underline font-semibold">$1</a>'
            );

            // 7. Headers
            html = html.replace(
                /^### (.*$)/gim,
                '<h5 class="text-sm font-bold mt-3 mb-1 font-slab">$1</h5>'
            );
            html = html.replace(
                /^## (.*$)/gim,
                '<h4 class="text-base font-bold mt-4 mb-2 font-slab">$1</h4>'
            );
            html = html.replace(
                /^# (.*$)/gim,
                '<h3 class="text-lg font-bold mt-5 mb-2 font-slab">$1</h3>'
            );

            // 8. Bullet Lists
            let lines = html.split("\n");
            let inList = false;
            for (let i = 0; i < lines.length; i++) {
                let line = lines[i].trim();
                if (line.startsWith("* ") || line.startsWith("- ")) {
                    let content = line.substring(2);
                    if (!inList) {
                        lines[i] =
                            '<ul class="list-disc pl-5 my-2 space-y-1 text-sm">\n<li>' +
                            content +
                            "</li>";
                        inList = true;
                    } else {
                        lines[i] = "<li>" + content + "</li>";
                    }
                } else if (inList) {
                    lines[i] = "</ul>\n" + lines[i];
                    inList = false;
                }
            }
            if (inList) {
                lines.push("</ul>");
            }
            html = lines.join("\n");

            // 9. Paragraph line breaks
            html = html.replace(/\n/g, "<br>");

            // 10. Auto-embed play iframe if the message contains a play view/edit URL
            const playUrlMatch = html.match(/\/plays\/show\?playId=(\d+)/);
            if (playUrlMatch) {
                const playId = playUrlMatch[1];
                html += `
                <div class="mt-4 border border-purple-200 rounded-lg overflow-hidden bg-white shadow-sm flex flex-col">
                    <div class="bg-purple-50 px-3 py-1.5 border-b border-purple-100 flex justify-between items-center text-xs">
                        <span class="font-semibold text-purple-900 flex items-center gap-1">
                            <ion-icon name="easel-outline" class="text-sm"></ion-icon>
                            Interactive Whiteboard Canvas
                        </span>
                        <a href="/plays/show?playId=${playId}" target="_blank" class="text-purple-700 hover:underline flex items-center gap-0.5">
                            Open Editor
                            <ion-icon name="open-outline" class="text-[10px]"></ion-icon>
                        </a>
                    </div>
                    <iframe src="/plays/show?playId=${playId}&readonly=true" width="100%" class="aspect-[2/0.96] border-0" frameborder="0"></iframe>
                </div>`;
            }

            return html;
        },
        escapeHtml(text) {
            return text
                .replace(/&/g, "&amp;")
                .replace(/</g, "&lt;")
                .replace(/>/g, "&gt;")
                .replace(/"/g, "&quot;")
                .replace(/'/g, "&#039;");
        },
        scrollToBottom() {
            const container = this.$refs.messagesContainer;
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        },
        adjustTextareaHeight(textarea) {
            if (!textarea) return;
            textarea.style.height = "auto";
            textarea.style.height = textarea.scrollHeight + "px";
        },
        formatTime(dateString) {
            if (!dateString) return "";
            const d = new Date(dateString);
            return d.toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
            });
        },
        formatTimeAgo(dateString) {
            if (!dateString) return "";
            const date = new Date(dateString);
            const seconds = Math.floor((new Date() - date) / 1000);

            let interval = Math.floor(seconds / 31536000);
            if (interval >= 1)
                return interval + " year" + (interval > 1 ? "s" : "") + " ago";
            interval = Math.floor(seconds / 2592000);
            if (interval >= 1)
                return interval + " month" + (interval > 1 ? "s" : "") + " ago";
            interval = Math.floor(seconds / 86400);
            if (interval >= 1)
                return interval + " day" + (interval > 1 ? "s" : "") + " ago";
            interval = Math.floor(seconds / 3600);
            if (interval >= 1)
                return interval + " hour" + (interval > 1 ? "s" : "") + " ago";
            interval = Math.floor(seconds / 60);
            if (interval >= 1)
                return (
                    interval + " minute" + (interval > 1 ? "s" : "") + " ago"
                );
            if (seconds < 10) return "just now";
            return Math.floor(seconds) + " seconds ago";
        },
    },
};
</script>

<style scoped>
.markdown-body :deep(ul) {
    list-style-type: disc !important;
}
</style>
